// Words for the cutdown dialog and review (mock-up 08). Pure so the labels
// that change with counts and verdicts are unit-tested in one place.

import {
  targetWords,
  type CutdownCreateResult,
  type CutdownLength,
  type CutdownSuggestion,
  type CutdownVerdict,
} from '../../../shared/cutdown';
import type { RuntimeEstimate } from '../../../shared/runtime';

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** A length is on offer only when the script runs longer than it. */
export function lengthAvailable(estimate: RuntimeEstimate | null, length: CutdownLength): boolean {
  return !!estimate && estimate.seconds > length;
}

export function lengthTileSub(estimate: RuntimeEstimate | null, length: CutdownLength): string {
  return lengthAvailable(estimate, length)
    ? `About ${targetWords(length)} words`
    : `Already under ${length}s`;
}

/** Why the editor's More menu item is off, or null when it's on. */
export function editorCutdownReason(estimate: RuntimeEstimate | null): string | null {
  if (!estimate) return 'Add more Audio first';
  if (estimate.seconds <= 15) return 'Already 15 seconds or shorter';
  return null;
}

export function suggestLabel(count: number): string {
  if (count === 0) return 'Choose a length';
  return `Suggest ${count} ${plural(count, 'cutdown', 'cutdowns')}`;
}

export function reviewLabel(ready: number, anyFailed: boolean): string {
  if (!anyFailed) return 'Review cuts';
  return `Review ${ready} ${plural(ready, 'cut', 'cuts')}`;
}

export function createLabel(count: number): string {
  if (count === 0) return 'Create drafts';
  return `Create ${count} ${plural(count, 'draft', 'drafts')}`;
}

export function reviewHeading(count: number, title: string): string {
  return `${count} ${plural(count, 'cutdown', 'cutdowns')} of ${title}`;
}

/** "57s": the review shows the unrounded runtime to the nearest second. */
export function shortSeconds(seconds: number): string {
  return `${Math.round(seconds)}s`;
}

export const VERDICT_LABELS: Record<CutdownVerdict, string> = {
  on_target: 'On target',
  over: 'Over target',
  under: 'Runs short',
};

/** "30s and 15s", "60s, 30s and 15s". */
function listLengths(lengths: CutdownLength[]): string {
  const parts = lengths.map((l) => `${l}s`);
  if (parts.length <= 1) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/**
 * The working step's failure box. `message` is the server's own line when
 * every failure carried the same non-AI reason (for example Workers AI not
 * being bound locally); otherwise the copy names the lengths.
 */
export function failureCopy(
  failed: CutdownLength[],
  ready: CutdownLength[],
  message: string | null,
): { lead: string; body: string } {
  const lead = message ??
    `Workers AI didn't answer for the ${listLengths(failed)} ${plural(failed.length, 'cut', 'cuts')}.`;
  if (ready.length === 0) return { lead, body: 'Your script is unchanged. Try again in a moment.' };
  const which = failed.length === 1 ? 'that length' : 'those lengths';
  const own = ready.length === 1 ? `the ${ready[0]}s on its own` : `the ${listLengths(ready)} cuts`;
  return { lead, body: `Your script is unchanged. Try ${which} again, or review ${own}.` };
}

export function retryLabel(failed: CutdownLength[]): string {
  return failed.length === 1 ? `Try ${failed[0]}s again` : 'Try again';
}

/**
 * The amber bar for a cut outside 15% of its target. Written from the
 * numbers, never by the model.
 */
export function missCopy(s: CutdownSuggestion): { lead: string; body: string } | null {
  if (s.verdict === 'on_target') return null;
  const runtime = shortSeconds(s.runtime_seconds);
  const kept = `The cut keeps ${s.kept_sentences} of ${s.total_sentences} sentences, and those run ${Math.round(s.runtime_seconds)} seconds together.`;
  if (s.verdict === 'over') {
    return {
      lead: `Closest: ${runtime} for ${s.seconds}s target.`,
      body: `${kept} Create it and trim by hand, or untick it.`,
    };
  }
  return {
    lead: `Runs short: ${runtime} for ${s.seconds}s target.`,
    body: `${kept} Create it and add lines back by hand, or untick it.`,
  };
}

/** Toast after create: names the project and offers the shortest draft. */
export function createdToast(result: CutdownCreateResult): {
  text: string;
  open: { id: string; label: string } | null;
} {
  const n = result.drafts.length;
  const shortest = [...result.drafts].sort((a, b) => a.seconds - b.seconds)[0];
  return {
    text: `${n} ${plural(n, 'draft', 'drafts')} created in ${result.project_name}`,
    open: shortest ? { id: shortest.id, label: `Open ${shortest.seconds}s` } : null,
  };
}
